import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Row, Col } from 'react-bootstrap';
import { FaTrash, FaEdit } from 'react-icons/fa';
import InputField from "../../../SalesForm/InputfieldSales";
import baseURL from "../../../../../../Url/NodeBaseURL";

const EstimateOldSalesForm = ({ 
  setOldSalesData, 
  oldTableData, 
  setOldTableData 
}) => {
  const [metalOptions, setMetalOptions] = useState([]);
  const [purityOptions, setPurityOptions] = useState([]);
  const [rates, setRates] = useState({
    rate_24crt: "",
    rate_22crt: "",
    rate_18crt: "",
    rate_16crt: "",
    rate_silver: "",
  });

  const [oldSalesFormData, setOldSalesFormData] = useState({
    product: "",
    metal: "",
    purity: "",
    purityPercentage: "",
    hsn_code: "",
    gross: "",
    dust: "",
    ml_percent: "",
    net_wt: "",
    remarks: "",
    rate: "",
    total_amount: "",
    total_old_amount: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  // Fetch metal types
  useEffect(() => {
    const fetchMetalTypes = async () => {
      try {
        const response = await fetch(`${baseURL}/metaltype`);
        if (!response.ok) {
          throw new Error("Failed to fetch metal types");
        }
        const metalTypes = await response.json();
        const formattedMetalTypes = metalTypes.map((item) => ({
          value: item.metal_name,
          label: item.metal_name,
          hsn_code: item.hsn_code,
        }));
        setMetalOptions(formattedMetalTypes);


      } catch (error) {
        console.error("Error fetching metal types:", error);
      }
    };

    fetchMetalTypes();
  }, []);

  // Fetch current rates
  useEffect(() => {
    const fetchCurrentRates = async () => {
      try {
        const response = await fetch(`${baseURL}/get/current-rates`);
        if (!response.ok) {
          throw new Error("Failed to fetch current rates");
        }
        const ratesData = await response.json();
        setRates({
          rate_24crt: ratesData.rate_24crt || "",
          rate_22crt: ratesData.rate_22crt || "",
          rate_18crt: ratesData.rate_18crt || "",
          rate_16crt: ratesData.rate_16crt || "",
          rate_silver: ratesData.silver_rate || "", // Add rate_silver from the response
        });
      } catch (error) {
        console.error("Error fetching current rates:", error);
      }
    };

    fetchCurrentRates();
  }, []);

  // Fetch and filter purity options
  useEffect(() => {
    const fetchAndFilterPurity = async () => {
      try {
        const response = await fetch(`${baseURL}/purity`);
        if (!response.ok) {
          throw new Error("Failed to fetch purity options");
        }
        const allPurityOptions = await response.json();

        const filteredPurityOptions = allPurityOptions.filter(
          (option) =>
            option.metal.toLowerCase() === oldSalesFormData.metal.toLowerCase()
        );

        const defaultPurityOption = filteredPurityOptions.find((option) =>
          option.name.toLowerCase().replace(/\s/g, "").includes("22k")
        );

        setPurityOptions(filteredPurityOptions);

        setOldSalesFormData((prevDetails) => {
          const updatedDetails = {
            ...prevDetails,
            purity: "Manual",
            purityPercentage: 0,
          };

          // **Ensure calculations happen immediately after purity change**
          updatedDetails.net_wt = calculateNetWeight(updatedDetails);
          updatedDetails.total_amount = calculateTotalAmount(updatedDetails);

          return updatedDetails;
        });
      } catch (error) {
        console.error("Error fetching purity options:", error);
      }
    };

    if (oldSalesFormData.metal) {
      fetchAndFilterPurity();
    }
  }, [oldSalesFormData.metal]);

  // Set ML percentage based on metal type
  useEffect(() => {
    if (oldSalesFormData.metal?.toLowerCase() === 'gold') {
      setOldSalesFormData((prevState) => ({
        ...prevState,
        ml_percent: 1, // Set default value for Gold
      }));
    } else if (oldSalesFormData.metal?.toLowerCase() === 'silver') {
      setOldSalesFormData((prevState) => ({
        ...prevState,
        ml_percent: 3, // Set default value for Silver
      }));
    } else if (!oldSalesFormData.metal) {
      setOldSalesFormData((prevState) => ({
        ...prevState,
        ml_percent: '', // Clear ml_percent if metal is cleared
      }));
    }
  }, [oldSalesFormData.metal]);

  // Auto-populate rate based on metal type and purity
  useEffect(() => {
    const normalizedMetal = oldSalesFormData.metal?.toLowerCase() || "";
    const normalizedPurity = normalizePurity(oldSalesFormData.purity || "");

    let updatedRate = 0;

    if (normalizedMetal === "silver") {
      updatedRate = rates.rate_silver || 0;
    } else if (normalizedMetal === "gold" && normalizedPurity === "manual") {
      updatedRate = rates.rate_24crt || 0;
    } else {
      updatedRate = normalizedPurity.includes("24")
        ? rates.rate_24crt
        : normalizedPurity.includes("22")
        ? rates.rate_22crt
        : normalizedPurity.includes("18")
        ? rates.rate_18crt
        : normalizedPurity.includes("16")
        ? rates.rate_16crt
        : rates.rate_22crt; // Default to rate_22crt if no match
    }

    setOldSalesFormData((prevDetails) => {
      const updatedDetails = { ...prevDetails, rate: updatedRate };

      // Immediately update net weight and total amount after setting rate
      updatedDetails.net_wt = calculateNetWeight({
        ...updatedDetails,
        purityPercentage: updatedDetails.purityPercentage,
      });

      updatedDetails.total_amount = calculateTotalAmount(updatedDetails);

      return updatedDetails;
    });
  }, [oldSalesFormData.metal, oldSalesFormData.purity, rates]);

  const normalizePurity = (purity) => purity.toLowerCase().replace(/\s+/g, "");

  const calculateNetWeight = ({
    gross,
    dust,
    purityPercentage,
    ml_percent,
  }) => {
    const grossNum = parseFloat(gross) || 0;
    const dustNum = parseFloat(dust) || 0;
    const purityNum = parseFloat(purityPercentage) || 0;
    const mlPercentNum = parseFloat(ml_percent) || 0;

    if (grossNum === 0 || purityNum === 0) return 0;

    const netWeight = (grossNum - dustNum) * (purityNum / 100) * (1 - mlPercentNum / 100);
    return netWeight;
  };

  const calculateTotalAmount = ({ net_wt, rate }) => {
    const netWeight = parseFloat(net_wt) || 0;
    const rateNum = parseFloat(rate) || 0;
    return netWeight * rateNum;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOldSalesFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-calculate net weight when relevant fields change
    if (['gross', 'dust', 'purityPercentage', 'ml_percent'].includes(name)) {
      const updatedData = {
        ...oldSalesFormData,
        [name]: value
      };
      
      const netWeight = calculateNetWeight(updatedData);
      const totalAmount = calculateTotalAmount({ 
        net_wt: netWeight, 
        rate: updatedData.rate 
      });

      setOldSalesFormData(prev => ({
        ...prev,
        [name]: value,
        net_wt: netWeight,
        total_amount: totalAmount
      }));
    }

    // Auto-calculate total amount when rate or net weight changes
    if (['rate', 'net_wt'].includes(name)) {
      const updatedData = {
        ...oldSalesFormData,
        [name]: value
      };
      
      const totalAmount = calculateTotalAmount(updatedData);
      setOldSalesFormData(prev => ({
        ...prev,
        [name]: value,
        total_amount: totalAmount
      }));
    }
  };

  const handleAdd = () => {
    if (isEditing) {
      // Update existing item
      const updatedData = [...oldTableData];
      updatedData[editingIndex] = {
        ...oldSalesFormData,
        id: Date.now()
      };
      setOldTableData(updatedData);
      setOldSalesData(updatedData);
      setIsEditing(false);
      setEditingIndex(null);
    } else {
      // Add new item
      const newItem = {
        ...oldSalesFormData,
        id: Date.now()
      };
      const updatedData = [...oldTableData, newItem];
      setOldTableData(updatedData);
      setOldSalesData(updatedData);
    }
    
    // Reset form
    setOldSalesFormData({
      product: "",
      metal: "",
      purity: "",
      purityPercentage: "",
      hsn_code: "",
      gross: 0,
      dust: 0,
      ml_percent: 0,
      net_wt: 0,
      remarks: "",
      rate: 0,
      total_amount: 0,
      total_old_amount: "",
    });
  };

  const handleEdit = (index) => {
    const item = oldTableData[index];
    setOldSalesFormData(item);
    setIsEditing(true);
    setEditingIndex(index);
  };

  const handleDelete = (index) => {
    const updatedData = oldTableData.filter((_, i) => i !== index);
    setOldTableData(updatedData);
    setOldSalesData(updatedData);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingIndex(null);
    setOldSalesFormData({
      product: "",
      metal: "",
      purity: "",
      purityPercentage: "",
      hsn_code: "",
      gross: 0,
      dust: 0,
      ml_percent: 0,
      net_wt: 0,
      remarks: "",
      rate: 0,
      total_amount: 0,
      total_old_amount: "",
    });
  };

  const calculateTotalSum = () => {
    return oldTableData.reduce((sum, item) => sum + (parseFloat(item.total_amount) || 0), 0);
  };

  return (
    <div className="estimate-old-sales-form">
      {/* <h5>URD Purchase Form</h5> */}
      
      {/* Form */}
      <Row className="mb-3">
        <Col xs={12} md={3}>
          <InputField
            label="Product"
            name="product"
            value={oldSalesFormData.product}
            onChange={handleInputChange}
          />
        </Col>
        <Col xs={12} md={3}>
          <InputField
            label="Metal"
            name="metal"
            type="select"
            value={oldSalesFormData.metal}
            onChange={handleInputChange}
            options={metalOptions.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
          />
        </Col>
        <Col xs={12} md={3}>
          <InputField
            label="Gross"
            name="gross"
            value={oldSalesFormData.gross || ""}
            onChange={handleInputChange}
          />
        </Col>
        <Col xs={12} md={3}>
          <InputField
            label="Dust"
            name="dust"
            value={oldSalesFormData.dust || ""}
            onChange={handleInputChange}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} md={3}>
          <InputField
            label="Purity"
            type="select"
            name="purity"
            value={oldSalesFormData.purity}
            onChange={handleInputChange}
            options={[
              ...purityOptions.map((purity) => ({
                value: purity.name,
                label: purity.name,
              })),
              { value: "Manual", label: "Manual" },
            ]}
          />
        </Col>

        {oldSalesFormData.purity === "Manual" && (
          <Col xs={12} md={3}>
            <InputField
              label="Custom Purity %"
              type="number"
              name="purityPercentage"
              value={oldSalesFormData.purityPercentage || ""}
              onChange={handleInputChange}
            />
          </Col>
        )}
        <Col xs={12} md={3}>
          <InputField
            label="ML %"
            name="ml_percent"
            value={oldSalesFormData.ml_percent}
            onChange={handleInputChange}
          />
        </Col>
        <Col xs={12} md={3}>
          <InputField
            label="Net Wt"
            name="net_wt"
            value={
              isNaN(oldSalesFormData.net_wt) || oldSalesFormData.net_wt === "" || oldSalesFormData.net_wt == 0
                ? ""
                : oldSalesFormData.net_wt
            }
            onChange={handleInputChange}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} md={3}>
          <InputField
            label="Rate"
            name="rate"
            value={oldSalesFormData.rate || ""}
            onChange={handleInputChange}
          />
        </Col>
        <Col xs={12} md={3}>
          <InputField
            label="Total Amt"
            name="total_amount"
            value={oldSalesFormData.total_amount == 0 ? "" : (Number(oldSalesFormData.total_amount) || 0).toFixed(2)}
            readOnly
          />
        </Col>
        <Col xs={12} md={3}>
          <InputField
            label="Remarks"
            name="remarks"
            value={oldSalesFormData.remarks}
            onChange={handleInputChange}
          />
        </Col>
        <Col xs={12} md={3} className="d-flex align-items-end">
          <div>
            <Button 
              variant="primary" 
              onClick={handleAdd}
              className="me-2"
              size="sm"
              style={{
                backgroundColor: "rgb(163, 110, 41)",
                borderColor: "rgb(163, 110, 41)",
              }}
            >
              {isEditing ? 'Update' : 'Add'}
            </Button>
            {isEditing && (
              <Button 
                variant="secondary" 
                onClick={handleCancel}
                size="sm"
              >
                Cancel
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table bordered hover size="sm">
          <thead>
            <tr>
              <th>SI</th>
              <th>Product</th>
              <th>Metal</th>
              <th>Gross</th>
              <th>Dust</th>
              <th>Purity</th>
              <th>ML %</th>
              <th>Net Wt</th>
              <th>Rate</th>
              <th>Total Amt</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {oldTableData.map((item, index) => (
              <tr key={item.id || index}>
                <td>{index + 1}</td>
                <td>{item.product}</td>
                <td>{item.metal}</td>
                <td>{item.gross}</td>
                <td>{item.dust}</td>
                <td>{item.purity}</td>
                <td>{item.ml_percent}</td>
                <td>{Number(item.net_wt).toFixed(3)}</td>
                <td>₹{item.rate}</td>
                <td>₹{Number(item.total_amount).toFixed(2)}</td>
                <td>{item.remarks}</td>
                <td>
                  <FaEdit
                    style={{ cursor: 'pointer', color: 'blue', marginRight: '10px' }}
                    onClick={() => handleEdit(index)}
                  />
                  <FaTrash
                    style={{ cursor: 'pointer', color: 'red' }}
                    onClick={() => handleDelete(index)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Total Summary */}
      {oldTableData.length > 0 && (
        <Row className="mt-3">
          <Col xs={12} className="text-end">
            <h6>Total Amount: ₹{calculateTotalSum().toFixed(2)}</h6>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default EstimateOldSalesForm; 